import { Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import MediaCard from "@/components/feature/MediaCard";
import { getMediaById } from "@/lib/media";
import { specials } from "@/mocks/specials";
import type { MediaItem } from "@/types/media";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function Special() {
  usePageMeta({
    title: "热门影视专题-555电影网",
    description:
      "555电影网热门影视专题策划，围绕科幻、悬疑、治愈、古装等题材精选片单，附推荐理由，帮你按兴趣发现好作品。",
    keywords: "影视专题,片单推荐,科幻专题,悬疑专题,555电影网",
  });

  return (
    <div className="mx-auto max-w-page px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "热门专题" }]} />

      <div className="mt-4 overflow-hidden rounded-xl bg-primary-800">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px]">
          <div className="p-6 md:p-10">
            <span className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-primary-950">
              <i className="ri-bookmark-3-line"></i>
              主题策划
            </span>
            <h1 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
              热门影视专题
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-primary-100 md:text-base">
              我们围绕题材、情绪与风格精心策划了多个观影专题，每一个专题都由编辑挑选作品并附上推荐理由，
              帮助你按兴趣快速找到真正想看的影视作品。
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {specials.map((special) => (
                <a
                  key={special.id}
                  href={`#${special.id}`}
                  className="whitespace-nowrap rounded-full border border-primary-600 bg-primary-900/40 px-4 py-1.5 text-xs text-primary-100 transition-colors hover:border-accent-400 hover:text-accent-400"
                >
                  {special.title.split("：")[0]}
                </a>
              ))}
            </div>
          </div>
          <div className="hidden h-full w-full lg:block">
            <img
              src={specials[0].cover}
              alt="专题精选封面"
              title="热门影视专题 - 555电影网"
              className="h-full w-full object-top"
            />
          </div>
        </div>
      </div>

      <div className="mt-10 space-y-16">
        {specials.map((special, index) => {
          const items = special.items
            .map((id) => getMediaById(id))
            .filter((item): item is MediaItem => Boolean(item));
          const related = special.related
            .map((id) => specials.find((s) => s.id === id))
            .filter((s): s is (typeof specials)[number] => Boolean(s));

          return (
            <section key={special.id} id={special.id} className="scroll-mt-20">
              <div
                className={`grid grid-cols-1 gap-6 lg:items-center lg:grid-cols-2 ${
                  index % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div className="h-56 w-full overflow-hidden rounded-xl bg-background-200 md:h-72">
                  <img
                    src={special.cover}
                    alt={`${special.title} 专题封面`}
                    title={`${special.title} - 555电影网`}
                    className="h-full w-full object-top"
                  />
                </div>
                <div>
                  <span className="text-xs font-medium uppercase tracking-wide text-accent-600">
                    专题策划 0{index + 1}
                  </span>
                  <h2 className="mt-2 font-heading text-xl font-bold text-foreground-900 md:text-2xl">
                    {special.title}
                  </h2>
                  <p className="mt-1 text-sm text-foreground-500">{special.subtitle}</p>
                  <p className="mt-4 leading-relaxed text-foreground-700">{special.intro}</p>

                  <div className="mt-5 rounded-lg border border-background-200 bg-background-100 p-4">
                    <h3 className="flex items-center gap-1.5 font-heading text-sm font-semibold text-foreground-900">
                      <i className="ri-lightbulb-flash-line text-accent-500"></i>
                      推荐理由
                    </h3>
                    <ul className="mt-3 space-y-2">
                      {special.reasons.map((reason) => (
                        <li key={reason} className="flex items-start gap-2 text-sm text-foreground-600">
                          <i className="ri-checkbox-circle-line mt-0.5 text-secondary-500"></i>
                          <span>{reason}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {related.length > 0 && (
                    <div className="mt-5 flex flex-wrap items-center gap-2">
                      <span className="text-xs text-foreground-400">相关专题：</span>
                      {related.map((rel) => (
                        <a
                          key={rel.id}
                          href={`#${rel.id}`}
                          className="whitespace-nowrap rounded-full bg-secondary-50 px-3 py-1 text-xs text-secondary-700 transition-colors hover:bg-secondary-100"
                        >
                          {rel.title.split("：")[0]}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6">
                <div className="mb-4 flex items-center gap-2">
                  <span className="h-4 w-1 rounded-full bg-accent-500"></span>
                  <h3 className="font-heading text-base font-semibold text-foreground-900">
                    专题片单 · 共 {items.length} 部
                  </h3>
                </div>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                  {items.map((item) => (
                    <MediaCard key={item.id} item={item} />
                  ))}
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <Link
                  to="/category/movie"
                  className="flex items-center gap-1 whitespace-nowrap text-sm text-secondary-600 transition-colors hover:text-secondary-700"
                >
                  浏览更多同类作品
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}